import { createSelector } from 'reselect'
import Immutable from 'immutable'
import {
    identity,
    EMPTY_STR,
    EMPTY_IMMUTABLE_MAP,
    EMPTY_OBJ,
    EMPTY_ARRAY
} from '@lacele/const'
import { REDUX_REDUCER_ADDED } from '../../Redux/types'
import { createStateSelector } from '../../Redux/utils'
import * as Types from './types'
import {
    getResponseTarget,
    getDataTarget,
    getMetaTarget,
    getStatusTarget,
    getErrorResponseTarget,
    getErrorTarget,
    createFetchedContentTargetSelector
} from './utils'
import {
    STATUS_REHYDRATED,
    STATUS_FETCH,
    STATUS_FETCHING,
    STATUS_FETCHED,
    STATUS_ERROR,
    STATUS_UNAUTHORIZED
} from '../const'

const fetchedContentTarget = createFetchedContentTargetSelector()

export const defaultState = Immutable.Map({
    [getStatusTarget()]: EMPTY_STR,
    [getMetaTarget()]: Immutable.Map()
})

export const getData = createSelector(
    identity,
    () => getDataTarget(),
    (state, target) => state.get(target)
)

export const getStatus = createSelector(
    identity,
    () => getStatusTarget(),
    (state, target) => state.get(target)
)

const canRequireFetch = (actualState) => {
    const status = getStatus(actualState) || STATUS_REHYDRATED
    switch (status) {
        case STATUS_REHYDRATED:
            return true
        case STATUS_FETCHED:
        case STATUS_ERROR:
            return !getData(actualState)
        default:
            return false
    }
}

export default (state, action) => {
    const actualState = state || defaultState
    switch (action.type) {
        case REDUX_REDUCER_ADDED:
            // Reset status when reducer is added
            return actualState.set(getStatusTarget(), STATUS_REHYDRATED)
        case Types.FETCH_REQUIRE: {
            if (canRequireFetch(actualState)) {
                // Force a new fetch if no data fetched or from an old session
                return actualState.set(getStatusTarget(), STATUS_FETCH)
            }
            return actualState
        }
        case Types.FETCH_FORCE:
            return actualState.set(getStatusTarget(), STATUS_FETCH)
        case Types.FETCH_META:
            return actualState.mergeIn([getMetaTarget()], action.payload)
        case Types.FETCH_PENDING:
            return actualState.set(getStatusTarget(), STATUS_FETCHING)
        case Types.FETCH_UNAUTHORIZED:
            return actualState.set(getStatusTarget(), STATUS_UNAUTHORIZED)
        case Types.FETCHED_DATA:
            if (getStatus(actualState) === STATUS_FETCHING) {
                // Change status to final only if currently fetching
                return actualState.set(getStatusTarget(), STATUS_FETCHED)
                    .set(getDataTarget(), action.payload.data)
                    .set(getResponseTarget(), action.payload.response)
                    .remove(getErrorTarget())
                    .remove(getErrorResponseTarget())
            }
            return actualState
        case Types.FETCHED_ERROR:
            if (getStatus(actualState) === STATUS_FETCHING) {
                // Change status to final only if currently fetching
                return actualState.set(getStatusTarget(), STATUS_ERROR)
                    .set(getErrorTarget(), action.payload.error)
                    .set(getErrorResponseTarget(), action.payload.response)
            }
            return actualState
        case Types.FETCHED_CLEAR:
            if (action.payload) {
                return actualState.deleteIn(action.payload)
            }
            return actualState.clear()
        default:
            return actualState
    }
}

export const getMeta = createSelector(
    identity,
    () => getMetaTarget(),
    (state, target) => state.get(target)
)

export const getResponse = createSelector(
    identity,
    () => getResponseTarget(),
    (state, target) => state.get(target)
)

export const getErrorResponse = createSelector(
    identity,
    () => getErrorResponseTarget(),
    (state, target) => state.get(target)
)

export const getError = createSelector(
    identity,
    () => getErrorTarget(),
    (state, target) => state.get(target)
)

export const getMetaAttribute = createSelector(
    getMeta,
    (_, attribute) => attribute,
    (meta, attribute) => (meta ? meta.get(attribute) : undefined)
)

// Client utilities

export const createDataSelector
    = (defValue) => createSelector(
        identity,
        (state) => state.get(getDataTarget()) || defValue
    )

export const createTargetPaginatorFetchedSelector
    = (getTargetFunc) => createSelector(
        createStateSelector(getTargetFunc),
        (state) => {
            const data = getData(state)
            return data ? {
                count: data.totalElements,
                page: data.number,
                size: data.size
            } : EMPTY_OBJ
        }
    )

export const createTargetStatusSelector
    = (getTargetFunc) => createSelector(
        (state) => state.get(getTargetFunc()) || EMPTY_IMMUTABLE_MAP,
        (state) => getStatus(state)
    )

export const createTargetFetchCompleteSelector
    = (getTargetFunc) => createSelector(
        createTargetStatusSelector(getTargetFunc),
        (status) => status === STATUS_FETCHED
            || status === STATUS_ERROR
            || status === STATUS_UNAUTHORIZED
    )

export const createTargetFetchSuccessSelector
    = (getTargetFunc) => createSelector(
        createTargetStatusSelector(getTargetFunc),
        (status) => status === STATUS_FETCHED
    )

export const createTargetResponseSelector
    = (getTargetFunc) => createSelector(
        (state) => state.get(getTargetFunc()) || EMPTY_IMMUTABLE_MAP,
        (state) => getResponse(state)
    )

export const createTargetErrorSelector
    = (getTargetFunc) => createSelector(
        (state) => state.get(getTargetFunc()) || EMPTY_IMMUTABLE_MAP,
        (state) => getError(state)
    )

export const createTargetDataSelector
    = (getTargetFunc, defValue) => createSelector(
        (state) => state.get(getTargetFunc()) || EMPTY_IMMUTABLE_MAP,
        (state) => getData(state) || defValue
    )

export const createTargetDataContentSelector
    = (getTargetFunc) => createSelector(
        createTargetDataSelector(getTargetFunc),
        () => fetchedContentTarget(),
        (data, contentTarget) => (data ? data[contentTarget] : EMPTY_ARRAY)
    )
